// src/components/auth/ProtectedRoute.tsx

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';
import type { UserRole } from '@/config/routes.config';
import { AVAILABLE_VIEWS } from '@/services/userViews.service';

interface ProtectedRouteProps {
  children: React.ReactNode;
  // Acepta un rol o lista de roles permitidos.
  // Las rutas en AVAILABLE_VIEWS siempre se validan contra permisos de vista (hasViewAccess).
  requiredRole?: UserRole | UserRole[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { isAuthenticated, isLoading, user, hasViewAccess } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-10 w-10 text-blue-400 animate-spin" />
          <p className="text-gray-400 text-sm">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user) {
    const userRole = user.roleSGC;
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    const hasRoleAccess = allowedRoles.includes(userRole);

    // Si la ruta está registrada en AVAILABLE_VIEWS, siempre validar permisos de vista
    // (hasViewAccess usa defaults por rol si no hay permisos personalizados)
    const view = AVAILABLE_VIEWS.find(v => v.path === location.pathname);

    if (view) {
      if (!hasViewAccess(view.id)) {
        return <Navigate to="/auto-redirect" replace />;
      }
    } else if (!hasRoleAccess) {
      return <Navigate to="/auto-redirect" replace />;
    }
  }

  return <>{children}</>;
};
